import React, {
  memo, useEffect, useRef, useState,
} from 'react';

import {
  TILE_CSS_H,
  TILE_CSS_W,
  colorForBox,
  colorForLabel,
  GLOBAL_IMAGE_CONCURRENCY,
  tileBoxesKey,
  withGlobalGate,
} from '../helpers';
import { fetchAssetBlob } from '../signedAssetCache';
import style from './style.module.scss';
import { useMapView } from './Map';

const PREVIEW = (tile) => tile?.webp || tile?.url || null;
const FULL = (tile) => tile?.url || null;
/** Stadium slots are tiny on screen — keep backing stores small. */
const SLOT_MAX_EDGE = 2048;
/** Single-region mosaic: CSS size × DPR, capped so one atlas stays reasonable. */
const REGION_MAX_EDGE = 4096;

const tileKeyOf = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

const fitAtlas = (cols, rows, maxEdge) => {
  const cssW = Math.max(1, cols * TILE_CSS_W);
  const cssH = Math.max(1, rows * TILE_CSS_H);
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  const nativeW = cssW * dpr;
  const nativeH = cssH * dpr;
  const fit = Math.min(1, maxEdge / nativeW, maxEdge / nativeH);
  const w = Math.max(1, Math.floor(nativeW * fit));
  const h = Math.max(1, Math.floor(nativeH * fit));
  return {
    w,
    h,
    cellW: w / cols,
    cellH: h / rows,
  };
};

/**
 * Batch stroke by label color — one beginPath/stroke per color.
 * Returns box count painted.
 */
const paintDetectionFrames = (ctx, list, cellW, cellH, tileBoxes, selectedLabels, prefix) => {
  if (!selectedLabels?.size || !tileBoxes) return 0;
  const stroke = Math.max(2, Math.min(cellW, cellH) * 0.006);
  const byColor = new Map();

  list.forEach((tile) => {
    const boxes = tileBoxes[tileBoxesKey(tile, prefix)] || [];
    if (!boxes.length) return;
    const dx = (Number(tile.col) || 0) * cellW;
    const dy = (Number(tile.row) || 0) * cellH;
    boxes.forEach((box) => {
      if (box?.BoundingBox && selectedLabels.has(box.label)) {
        const {
          Left = 0, Top = 0, Width = 0, Height = 0,
        } = box.BoundingBox;
        const bw = Width * cellW;
        const bh = Height * cellH;
        if (bw >= 0.5 && bh >= 0.5) {
          const color = colorForBox(box);
          let flat = byColor.get(color);
          if (!flat) {
            flat = [];
            byColor.set(color, flat);
          }
          flat.push(dx + Left * cellW, dy + Top * cellH, bw, bh);
        }
      }
    });
  });

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

/**
 * Paint seats onto a dedicated layer (Figma-style).
 * Uses mosaic CSS coords (xPx/yPx) → atlas pixels. No image dependency.
 * Toggle = CSS visibility on this canvas — never re-run this for show/hide.
 */
const paintSrsLayer = (ctx, w, h, cellW, cellH, overlay, scanId) => {
  ctx.clearRect(0, 0, w, h);
  const seats = (scanId && overlay?.byScan?.[scanId]) || overlay?.seats;
  if (!seats?.length || cellW <= 0 || cellH <= 0) return 0;

  const kx = cellW / TILE_CSS_W;
  const ky = cellH / TILE_CSS_H;
  const stroke = Math.max(1, Math.min(cellW, cellH) * 0.004);

  const byColor = new Map();
  seats.forEach((s) => {
    if (scanId && s.scanId && s.scanId !== scanId) return;
    const color = colorForLabel(s.section || '?');
    let flat = byColor.get(color);
    if (!flat) {
      flat = [];
      byColor.set(color, flat);
    }
    const side = Math.max(1, (s.sizePx || 8) * kx);
    const cx = (Number(s.xPx) || 0) * kx;
    const cy = (Number(s.yPx) || 0) * ky;
    flat.push(cx - side / 2, cy - side / 2, side, side);
  });

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

const closeBitmap = (bmp) => {
  if (bmp && typeof bmp.close === 'function') {
    try { bmp.close(); } catch { /* already closed */ }
  }
};

const loadImageFromBlob = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('img error'));
  };
  img.src = url;
});

const decodeTileBitmap = async (blob, resizeW, resizeH) => {
  const w = Math.max(1, Math.round(Number(resizeW) || 0));
  const h = Math.max(1, Math.round(Number(resizeH) || 0));
  if (typeof createImageBitmap === 'function') {
    if (w > 1 && h > 1) {
      try {
        return await createImageBitmap(blob, {
          resizeWidth: w,
          resizeHeight: h,
          resizeQuality: 'low',
        });
      } catch {
        return createImageBitmap(blob);
      }
    }
    return createImageBitmap(blob);
  }
  return loadImageFromBlob(blob);
};

const loadTileBitmap = (url, resizeW, resizeH) => (
  withGlobalGate('crowd-count-images', GLOBAL_IMAGE_CONCURRENCY, async () => {
    if (!url) throw new Error('no url');
    const blob = await fetchAssetBlob(url);
    if (!blob) throw new Error('no blob');
    return decodeTileBitmap(blob, resizeW, resizeH);
  })
);

const loadOneTile = async (tile, cellW, cellH) => {
  const signed = PREVIEW(tile);
  if (!signed) return null;
  try {
    try {
      return await loadTileBitmap(signed, cellW, cellH);
    } catch {
      const full = FULL(tile);
      if (full && full !== signed) {
        return loadTileBitmap(full, cellW, cellH);
      }
      return null;
    }
  } catch {
    return null;
  }
};

/**
 * Two layers: base (tiles/boxes) + SRS (pre-baked seats).
 * SRS toggle is CSS visibility — compositor only, no React paint work.
 *
 * Stadium: atlas is CSS×DPR (capped), tiles decode at cell size, bitmap is
 * closed after blit so full-res Image() objects never accumulate.
 */
const MosaicCanvas = ({
  tiles,
  rows,
  cols,
  loading = false,
  tileBoxes = null,
  selectedLabels = null,
  boxKeyPrefix = null,
  /** Clears image cache only when this changes (selected scan/option). */
  scanKey = '',
  viewportLoad = true,
  overlay = null,
  maxEdge = REGION_MAX_EDGE,
  bakeSrs = true,
  onSettled = null,
}) => {
  const canvasRef = useRef(null);
  const boxesCanvasRef = useRef(null);
  const srsCanvasRef = useRef(null);
  const atlasRef = useRef({
    w: 0, h: 0, cellW: 0, cellH: 0,
  });
  const paintedRef = useRef(new Set());
  const failedRef = useRef(new Set());
  const inflightRef = useRef(new Set());
  const settledRef = useRef(false);
  const genRef = useRef(0);
  const [paintedCount, setPaintedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [atlasEpoch, setAtlasEpoch] = useState(0);
  const [wanted, setWanted] = useState(() => new Set());
  const { viewportEl } = useMapView();

  const list = tiles || [];
  const r = Number(rows) || 0;
  const c = Number(cols) || 0;
  const scanId = boxKeyPrefix || null;
  const listRef = useRef(list);
  listRef.current = list;
  const edge = Number(maxEdge) > 0 ? Number(maxEdge) : REGION_MAX_EDGE;

  const syncAtlas = () => {
    if (r < 1 || c < 1) return atlasRef.current;
    const next = fitAtlas(c, r, edge);
    const prev = atlasRef.current;
    if (prev.w !== next.w || prev.h !== next.h) {
      atlasRef.current = next;
    }
    return atlasRef.current;
  };

  useEffect(() => {
    genRef.current += 1;
    paintedRef.current = new Set();
    failedRef.current = new Set();
    inflightRef.current = new Set();
    settledRef.current = false;
    atlasRef.current = {
      w: 0, h: 0, cellW: 0, cellH: 0,
    };
    setWanted(new Set());
    setPaintedCount(0);
    setFailedCount(0);
    return () => { genRef.current += 1; };
  }, [scanKey]);

  // Single-region: request every tile. Do not depend on viewportEl (it mounts async
  // and was re-firing this effect, canceling in-flight loads → permanent gaps).
  useEffect(() => {
    if (viewportLoad || loading || !list.length || r < 1 || c < 1) return;
    const all = new Set(list.map(tileKeyOf));
    setWanted((prev) => {
      if (prev.size === all.size) {
        let same = true;
        all.forEach((k) => { if (!prev.has(k)) same = false; });
        if (same) return prev;
      }
      return all;
    });
  }, [viewportLoad, loading, scanKey, list.length, r, c]);

  // Stadium: grow wanted as sentinels intersect the map viewport.
  useEffect(() => {
    if (!viewportLoad || loading || !list.length || r < 1 || c < 1) return undefined;
    const root = canvasRef.current?.parentElement;
    if (!root) return undefined;

    let alive = true;
    const io = new IntersectionObserver(
      (entries) => {
        if (!alive) return;
        const hit = [];
        entries.forEach((entry) => {
          const key = entry.target.dataset.loadKey;
          if (!key || !entry.isIntersecting) return;
          hit.push(key);
        });
        if (!hit.length) return;
        setWanted((prev) => {
          let changed = false;
          const next = new Set(prev);
          hit.forEach((key) => {
            if (!next.has(key)) {
              next.add(key);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      },
      { root: viewportEl || null, rootMargin: '30%', threshold: 0 },
    );

    root.querySelectorAll('[data-load-key]').forEach((el) => io.observe(el));
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [viewportLoad, loading, scanKey, list.length, r, c, viewportEl]);

  // Size base canvas from CSS×DPR (not native 1280×720 cells) and fill placeholders.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || loading || r < 1 || c < 1) return undefined;
    const { w, h } = syncAtlas();
    const sizeChanged = canvas.width !== w || canvas.height !== h;
    if (sizeChanged || paintedRef.current.size === 0) {
      canvas.width = w;
      canvas.height = h;
      paintedRef.current = new Set();
      failedRef.current = new Set();
      setPaintedCount(0);
      setFailedCount(0);
      const ctx = canvas.getContext('2d', { alpha: false });
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#1a1d22';
        ctx.fillRect(0, 0, w, h);
      }
      if (sizeChanged) setAtlasEpoch((n) => n + 1);
    }
    return undefined;
  }, [loading, r, c, edge, scanKey]);

  // Decode wanted tiles at cell size, blit once, close the bitmap.
  useEffect(() => {
    if (loading || !wanted.size || r < 1 || c < 1) return;
    const gen = genRef.current;
    const { cellW, cellH } = syncAtlas();
    if (cellW <= 0 || cellH <= 0) return;

    wanted.forEach((key) => {
      if (
        paintedRef.current.has(key)
        || failedRef.current.has(key)
        || inflightRef.current.has(key)
      ) return;
      const tile = listRef.current.find((t) => tileKeyOf(t) === key);
      if (!tile) {
        failedRef.current.add(key);
        setFailedCount(failedRef.current.size);
        return;
      }
      inflightRef.current.add(key);
      loadOneTile(tile, cellW, cellH).then((img) => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) {
          closeBitmap(img);
          return;
        }
        const canvas = canvasRef.current;
        if (!canvas || !img) {
          closeBitmap(img);
          failedRef.current.add(key);
          setFailedCount(failedRef.current.size);
          return;
        }
        const ctx = canvas.getContext('2d', { alpha: false });
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'low';
          const dx = (Number(tile.col) || 0) * cellW;
          const dy = (Number(tile.row) || 0) * cellH;
          ctx.drawImage(img, dx, dy, cellW, cellH);
        }
        closeBitmap(img);
        paintedRef.current.add(key);
        setPaintedCount(paintedRef.current.size);
      }).catch(() => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) return;
        failedRef.current.add(key);
        setFailedCount(failedRef.current.size);
      });
    });
  }, [wanted, loading, scanKey, r, c, edge, atlasEpoch]);

  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (loading || settledRef.current) return;
    const notify = onSettledRef.current;
    if (!notify) return;
    if (!list.length) {
      settledRef.current = true;
      notify();
      return;
    }
    if (inflightRef.current.size > 0) return;
    if (wanted.size < list.length) return;
    const done = paintedRef.current.size + failedRef.current.size;
    if (done < wanted.size) return;
    settledRef.current = true;
    notify();
  }, [loading, list.length, wanted, paintedCount, failedCount]);

  const needBoxes = Boolean(selectedLabels?.size);
  const needSrs = Boolean(bakeSrs && overlay?.seats?.length);

  // Boxes layer: selection change / detections — never redraws mosaic images.
  // Visibility is CSS [data-boxes-show]; this always keeps the bitmap ready.
  useEffect(() => {
    const canvas = boxesCanvasRef.current;
    if (!canvas || loading || r < 1 || c < 1 || !needBoxes) return;
    const {
      w, h, cellW, cellH,
    } = syncAtlas();

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    paintDetectionFrames(
      ctx,
      list,
      cellW,
      cellH,
      tileBoxes,
      selectedLabels,
      boxKeyPrefix,
    );
  }, [
    loading, list, r, c, tileBoxes, selectedLabels, boxKeyPrefix, atlasEpoch, needBoxes, edge,
  ]);

  // SRS layer: bake once when overlay / atlas size ready. Toggle does not touch this.
  useEffect(() => {
    const canvas = srsCanvasRef.current;
    if (!canvas || loading || r < 1 || c < 1 || !needSrs) return;
    const {
      w, h, cellW, cellH,
    } = syncAtlas();

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    paintSrsLayer(ctx, w, h, cellW, cellH, overlay, scanId);
  }, [loading, overlay, scanId, r, c, atlasEpoch, needSrs, edge]);

  const hasAnyImage = paintedCount > 0;
  const showLoading = loading || (!hasAnyImage && list.length > 0);

  return (
    <div className={style.mosaicLayer} aria-hidden={false}>
      <canvas
        ref={canvasRef}
        className={style.mosaicCanvas}
      />
      <div className={style.decorativeCanvases} aria-hidden="true">
        {needBoxes ? (
          <canvas
            ref={boxesCanvasRef}
            className={style.boxesCanvas}
          />
        ) : null}
        {needSrs ? (
          <canvas
            ref={srsCanvasRef}
            className={style.srsCanvas}
          />
        ) : null}
      </div>
      {viewportLoad && !loading && list.length > 0 ? (
        <div
          className={style.loadSentinels}
          style={{
            gridTemplateColumns: `repeat(${c}, ${TILE_CSS_W}px)`,
            gridTemplateRows: `repeat(${r}, ${TILE_CSS_H}px)`,
          }}
        >
          {list.map((tile) => {
            const key = tileKeyOf(tile);
            return (
              <div
                key={boxKeyPrefix ? `${boxKeyPrefix}:${key}` : key}
                data-load-key={key}
                className={style.loadSentinel}
              />
            );
          })}
        </div>
      ) : null}
      {showLoading ? <div className={style.mosaicLoading} /> : null}
    </div>
  );
};

export default memo(MosaicCanvas);
export {
  SLOT_MAX_EDGE,
  REGION_MAX_EDGE,
  fitAtlas,
  paintDetectionFrames,
  paintSrsLayer,
};
